import React, { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter as Router,
  Link,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import "./styles/App.css";

const STORAGE_KEY = "scope-change-shield-cases-v1";

const stopWords = new Set([
  "the",
  "and",
  "for",
  "you",
  "with",
  "that",
  "this",
  "from",
  "are",
  "our",
  "can",
  "please",
  "need",
  "would",
  "could",
  "have",
  "into",
  "just",
  "about",
  "your",
]);

const sampleThread =
  "Client: The launch pages look good, but can you also add three new service pages, refresh the pricing section, and turn the case study into a downloadable PDF by Thursday? We know this was not in the first brief, but it should be quick. Also, the founder wants another round of homepage concepts before approval.";

function readCases() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch (error) {
    return [];
  }
}

function writeCases(cases) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cases));
}

function sentenceSplit(text) {
  return text
    .replace(/\s+/g, " ")
    .split(/(?<=[.!?])\s+|\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function importantTerms(text) {
  const counts = {};
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .split(/\s+/)
    .filter((word) => word.length > 3 && !stopWords.has(word))
    .forEach((word) => {
      counts[word] = (counts[word] || 0) + 1;
    });

  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([word]) => word);
}

function simpleHash(value) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash).toString(36).slice(0, 5).toUpperCase();
}

function detectClientName(text) {
  const labeled = text.match(/(?:client|customer|from)\s*[:-]\s*([A-Z][A-Za-z0-9 &.'-]{2,32})/i);
  if (labeled) return labeled[1].replace(/[.!?].*$/, "").trim();
  const company = text.match(/\b((?!Client\b|Customer\b|From\b)[A-Z][A-Za-z0-9&.'-]+(?:\s+[A-Z][A-Za-z0-9&.'-]+){0,2})\b/);
  return company ? company[1].trim() : "Client request";
}

function containsAny(value, words) {
  const lower = value.toLowerCase();
  return words.some((word) => lower.includes(word));
}

function buildAnalysis({ thread, anchor, agency }) {
  const now = new Date();
  const cleanThread = thread.trim();
  const sentences = sentenceSplit(cleanThread);
  const terms = importantTerms(cleanThread);
  const scopeWords = [
    "also",
    "add",
    "new",
    "extra",
    "another",
    "revise",
    "revision",
    "refresh",
    "change",
    "quick",
    "before approval",
    "not in",
    "was not",
    "by tomorrow",
    "by thursday",
    "by friday",
    "urgent",
    "asap",
    "same budget",
  ];
  const timelineWords = ["today", "tomorrow", "this week", "by thursday", "by friday", "asap", "urgent", "before approval"];
  const relationshipWords = ["quick", "simple", "just", "should be", "not in", "same budget", "approval"];
  const selectedAsks = sentences.filter((sentence) => containsAny(sentence, scopeWords)).slice(0, 5);
  const asks = selectedAsks.length > 0 ? selectedAsks : sentences.slice(0, 3);
  const timelineHits = sentences.filter((sentence) => containsAny(sentence, timelineWords));
  const relationshipHits = sentences.filter((sentence) => containsAny(sentence, relationshipWords));
  const scopeScore = Math.min(10, asks.length * 2 + (terms.includes("add") ? 1 : 0));
  const timelineScore = Math.min(10, timelineHits.length * 3 + (containsAny(cleanThread, ["asap", "urgent"]) ? 2 : 0));
  const relationshipScore = Math.min(10, relationshipHits.length * 2);
  const total = scopeScore + timelineScore + relationshipScore;
  const severity = total >= 20 ? "Protect margin now" : total >= 12 ? "Clarify before agreeing" : "Light guardrail";
  const priority = total >= 20 ? "High" : total >= 12 ? "Medium" : "Watch";
  const confidence = Math.min(94, 58 + total * 2);
  const detectedClient = detectClientName(cleanThread);
  const agencyName = agency.trim() || "Your team";
  const scopeAnchor = anchor.trim() || "the approved project scope, timeline, and revision path";
  const id = `SCS-${now.toISOString().slice(2, 10).replace(/-/g, "")}-${simpleHash(cleanThread + scopeAnchor + now.toISOString())}`;
  const firstAsk = asks[0] || cleanThread.slice(0, 160);
  const impactEstimate =
    total >= 20
      ? "Likely added fee or timeline change before work starts"
      : total >= 12
        ? "Clarification needed before commitment"
        : "Track as a small scope note unless it repeats";
  const responseLead =
    priority === "High"
      ? "This looks doable, and it changes the current scope enough that I want to protect the schedule before we start."
      : "This may be doable, and I want to make sure we keep the approved plan clear before I confirm.";

  return {
    id,
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
    product: "Scope Change Shield",
    clientName: detectedClient,
    agencyName,
    originalThread: cleanThread,
    scopeAnchor,
    title: `${detectedClient} scope review`,
    priority,
    severity,
    confidence,
    impactEstimate,
    terms,
    detectedAsks: asks.map((ask, index) => ({
      id: `${id}-ask-${index + 1}`,
      text: ask,
      type: containsAny(ask, ["new", "add", "also", "extra"]) ? "Added deliverable" : "Scope pressure",
      risk: containsAny(ask, timelineWords) ? "Timing pressure" : "Margin pressure",
    })),
    signals: [
      `${asks.length} request${asks.length === 1 ? "" : "s"} need a scope decision.`,
      `${timelineHits.length || 1} timing cue${timelineHits.length === 1 ? "" : "s"} found.`,
      `${relationshipHits.length || 1} phrase${relationshipHits.length === 1 ? "" : "s"} could soften the boundary if left unanswered.`,
    ],
    brief: [
      `Anchor the reply to ${scopeAnchor}.`,
      `Treat "${firstAsk.slice(0, 120)}${firstAsk.length > 120 ? "..." : ""}" as the lead scope item.`,
      impactEstimate,
      "Offer a clear path: approve a small change order, swap it for another item, or defer it to a follow-up phase.",
    ],
    proofGaps: [
      "Which approved deliverables are being replaced, if any?",
      "Is there a hard date or just a preference?",
      "Who can approve added budget or timeline movement?",
      "What work should pause until the change is accepted?",
    ],
    nextAction:
      priority === "High"
        ? "Reply today with the boundary note, then send a priced change-order option before production work begins."
        : "Clarify the requested change and ask the client to choose between budget, timeline, or scope tradeoffs.",
    responseSequence: [
      {
        label: "Acknowledge",
        copy: `${responseLead} I can see why this matters for the launch, and we can map the cleanest path from here.`,
      },
      {
        label: "Protect the plan",
        copy: `The current agreement is based on ${scopeAnchor}. The new request adds work around ${terms.slice(0, 3).join(", ") || "the latest items"}, so I do not want to fold it in silently and surprise anyone later.`,
      },
      {
        label: "Offer the choice",
        copy: `Best next step: I can price this as a focused change, swap it against an approved item, or schedule it for the next phase. My recommendation is to decide that before the team starts the added work.`,
      },
    ],
    summary:
      `${agencyName} should not approve this request as a quiet favor. The safest path is to acknowledge the client need, name the change from the approved scope, and offer a budget, timeline, or phase tradeoff.`,
  };
}

function saveCase(caseRecord) {
  const existing = readCases().filter((item) => item.id !== caseRecord.id);
  const next = [caseRecord, ...existing].slice(0, 25);
  writeCases(next);
  return next;
}

function Nav() {
  return (
    <header className="topbar">
      <Link className="brand" to="/" aria-label="Scope Change Shield home">
        <span className="brand-mark">SC</span>
        <span>
          <strong>Scope Change Shield</strong>
          <small>Client Services Operations</small>
        </span>
      </Link>
      <nav className="navlinks" aria-label="Primary navigation">
        <Link to="/history">History</Link>
        <Link to="/pricing">Pricing</Link>
      </nav>
    </header>
  );
}

function Metric({ label, value }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function LandingPage() {
  const navigate = useNavigate();
  const [thread, setThread] = useState("");
  const [anchor, setAnchor] = useState("");
  const [agency, setAgency] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [latestCase, setLatestCase] = useState(null);
  const historyCount = useMemo(() => readCases().length, []);

  function handleSubmit(event) {
    event.preventDefault();
    setError("");
    if (thread.trim().length < 80) {
      setError("Paste at least a few client sentences so the shield can find scope pressure, timing, and reply language.");
      return;
    }
    setLoading(true);
    window.setTimeout(() => {
      const record = buildAnalysis({ thread, anchor, agency });
      saveCase(record);
      setLatestCase(record);
      setLoading(false);
      navigate(`/detail/${record.id}`);
    }, 450);
  }

  return (
    <main>
      <section className="hero-section">
        <div className="hero-copy">
          <p className="eyebrow">For small agencies, studios, consultants, and freelancers</p>
          <h1>Turn a risky client ask into a clear change-order response.</h1>
          <p className="hero-lede">
            Paste the latest client thread. Get a prioritized scope brief, three-step reply, next action, durable case ID,
            and saved history in one run.
          </p>
          <div className="hero-actions">
            <a href="#shield-form" className="primary-link">Protect a project</a>
            <Link to="/history" className="secondary-link">Reopen saved work</Link>
          </div>
          <div className="workflow-strip" aria-label="Workflow signal">
            <span>Paste request</span>
            <span>Rank scope risk</span>
            <span>Send clearer reply</span>
          </div>
        </div>
        <div className="hero-visual" aria-label="Scope packet preview">
          <div className="risk-board">
            <div>
              <span className="tiny-label">Scope pressure</span>
              <strong>Added deliverables before approval</strong>
            </div>
            <div className="risk-line high"></div>
            <div className="risk-line medium"></div>
            <div className="risk-line low"></div>
            <p>Recommended stance: clarify before accepting new work.</p>
          </div>
        </div>
      </section>

      <section className="workspace-band" id="shield-form">
        <div className="workspace-copy">
          <p className="eyebrow">One required field</p>
          <h2>Paste the request. Walk away with the packet.</h2>
          <p>
            The first version uses browser-local storage. It does not send emails, connect accounts, charge cards, or store
            shared team data.
          </p>
          <div className="stats-row">
            <Metric label="Saved cases" value={historyCount} />
            <Metric label="Required clicks" value="1" />
            <Metric label="Required fields" value="1" />
          </div>
        </div>

        <form className="shield-form" onSubmit={handleSubmit}>
          <label>
            Client request thread
            <textarea
              value={thread}
              onChange={(event) => setThread(event.target.value)}
              placeholder="Paste the latest client email, chat thread, meeting notes, or request list here."
              rows={8}
            />
          </label>
          <div className="form-grid">
            <label>
              Original project promise
              <input
                value={anchor}
                onChange={(event) => setAnchor(event.target.value)}
                placeholder="Example: launch site plus two revision rounds"
              />
            </label>
            <label>
              Business name
              <input
                value={agency}
                onChange={(event) => setAgency(event.target.value)}
                placeholder="Example: Northline Studio"
              />
            </label>
          </div>
          {error && <p className="form-error" role="alert">{error}</p>}
          <div className="form-actions">
            <button className="primary-button" type="submit" disabled={loading}>
              {loading ? "Building shield..." : "Protect this project"}
            </button>
            <button className="ghost-button" type="button" onClick={() => setThread(sampleThread)}>
              Load example
            </button>
            <button
              className="ghost-button"
              type="button"
              onClick={() => {
                setThread("");
                setAnchor("");
                setAgency("");
                setError("");
                setLatestCase(null);
              }}
            >
              Clear
            </button>
          </div>
          {latestCase && (
            <div className="inline-result">
              <strong>{latestCase.id}</strong>
              <span>{latestCase.nextAction}</span>
            </div>
          )}
        </form>
      </section>

      <section className="value-band">
        <div>
          <h2>What the packet includes</h2>
          <p>Every submission prepares the full result, detail, history row, public-facing summary, and next action.</p>
        </div>
        <div className="feature-grid">
          <article>
            <h3>Scope priority</h3>
            <p>Ranks added deliverables, timing pressure, and relationship risk from the pasted request.</p>
          </article>
          <article>
            <h3>Change-order brief</h3>
            <p>Converts messy messages into an internal brief your team can act on.</p>
          </article>
          <article>
            <h3>Client-ready sequence</h3>
            <p>Builds a three-step response that acknowledges the ask, protects scope, and offers choices.</p>
          </article>
        </div>
      </section>
    </main>
  );
}

function CaseDetail() {
  const { id } = useParams();
  const record = readCases().find((item) => item.id === id);

  if (!record) {
    return (
      <main className="narrow-page">
        <div className="empty-state">
          <h1>Case not found</h1>
          <p>This browser does not have a saved shield with that ID.</p>
          <Link className="primary-link" to="/">Create a new shield</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="detail-layout">
      <section className="detail-header">
        <div>
          <p className="eyebrow">{record.id}</p>
          <h1>{record.title}</h1>
          <p>{record.summary}</p>
        </div>
        <div className={`priority-badge ${record.priority.toLowerCase()}`}>{record.priority}</div>
      </section>

      <section className="detail-grid">
        <article className="detail-panel">
          <h2>Recommended stance</h2>
          <p className="big-answer">{record.severity}</p>
          <p>{record.impactEstimate}</p>
          <div className="stats-row tight">
            <Metric label="Confidence" value={`${record.confidence}%`} />
            <Metric label="Asks found" value={record.detectedAsks.length} />
          </div>
        </article>
        <article className="detail-panel">
          <h2>Next action</h2>
          <p className="big-answer">{record.nextAction}</p>
          <Link className="secondary-link compact" to={`/summary/${record.id}`}>Open client summary</Link>
        </article>
      </section>

      <section className="detail-panel full">
        <h2>Scope items</h2>
        <div className="ask-list">
          {record.detectedAsks.map((ask) => (
            <div className="ask-item" key={ask.id}>
              <span>{ask.type}</span>
              <strong>{ask.risk}</strong>
              <p>{ask.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="detail-grid">
        <article className="detail-panel">
          <h2>Change-order brief</h2>
          <ul className="plain-list">
            {record.brief.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </article>
        <article className="detail-panel">
          <h2>Proof checklist</h2>
          <ul className="plain-list">
            {record.proofGaps.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </article>
      </section>

      <section className="detail-panel full">
        <h2>Client-ready response</h2>
        <div className="sequence-grid">
          {record.responseSequence.map((step) => (
            <article key={step.label}>
              <span>{step.label}</span>
              <p>{step.copy}</p>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

function HistoryPage() {
  const [cases, setCases] = useState([]);

  useEffect(() => {
    setCases(readCases());
  }, []);

  function clearHistory() {
    writeCases([]);
    setCases([]);
  }

  return (
    <main className="narrow-page">
      <section className="page-heading">
        <div>
          <p className="eyebrow">Saved work</p>
          <h1>Scope history</h1>
          <p>Reopen any locally saved shield from this browser.</p>
        </div>
        {cases.length > 0 && (
          <button className="ghost-button" type="button" onClick={clearHistory}>Clear history</button>
        )}
      </section>

      {cases.length === 0 ? (
        <div className="empty-state">
          <h2>No saved shields yet</h2>
          <p>Paste a client request on the home page to create the first durable case.</p>
          <Link className="primary-link" to="/">Start a shield</Link>
        </div>
      ) : (
        <div className="history-list">
          {cases.map((item) => (
            <Link className="history-row" to={`/detail/${item.id}`} key={item.id}>
              <span>{item.id}</span>
              <strong>{item.title}</strong>
              <p>{item.nextAction}</p>
              <em>{new Date(item.createdAt).toLocaleString()}</em>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}

function SummaryPage() {
  const { id } = useParams();
  const record = readCases().find((item) => item.id === id);

  if (!record) return <Navigate to="/history" replace />;

  return (
    <main className="summary-page">
      <section className="summary-sheet">
        <p className="eyebrow">Client-facing summary</p>
        <h1>{record.clientName}: proposed change path</h1>
        <p className="summary-lede">{record.summary}</p>
        <div className="summary-block">
          <h2>Recommended path</h2>
          <p>{record.nextAction}</p>
        </div>
        <div className="summary-block">
          <h2>Lead request</h2>
          <p>{record.detectedAsks[0]?.text || record.originalThread}</p>
        </div>
        <div className="summary-block">
          <h2>Reply sequence</h2>
          {record.responseSequence.map((step) => (
            <p key={step.label}><strong>{step.label}:</strong> {step.copy}</p>
          ))}
        </div>
        <div className="summary-footer">
          <span>{record.id}</span>
          <Link to={`/detail/${record.id}`}>Back to detail</Link>
        </div>
      </section>
    </main>
  );
}

function PricingPage() {
  const plans = [
    {
      name: "Starter",
      price: "$19",
      buyer: "Solo operators",
      points: ["10 shields per month", "Local case history", "Client response packs"],
    },
    {
      name: "Studio",
      price: "$49",
      buyer: "Small teams",
      points: ["Unlimited shields", "Team review workflow roadmap", "Reusable reply library roadmap"],
    },
    {
      name: "Ops",
      price: "$149",
      buyer: "Multi-project agencies",
      points: ["Import roadmap", "Manager reporting roadmap", "Scope policy onboarding"],
    },
  ];

  return (
    <main className="pricing-page">
      <section className="page-heading">
        <div>
          <p className="eyebrow">Pricing</p>
          <h1>Protect one margin leak and the product pays for itself.</h1>
          <p>This benchmark build shows the commercial surface only. It does not process live payments.</p>
        </div>
      </section>
      <div className="pricing-grid">
        {plans.map((plan) => (
          <article className="price-card" key={plan.name}>
            <span>{plan.buyer}</span>
            <h2>{plan.name}</h2>
            <strong>{plan.price}<small>/mo</small></strong>
            <ul className="plain-list">
              {plan.points.map((point) => <li key={point}>{point}</li>)}
            </ul>
            <Link className="primary-link" to="/">Start with pasted request</Link>
          </article>
        ))}
      </div>
    </main>
  );
}

function AppShell() {
  return (
    <div className="app-shell">
      <Nav />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/detail/:id" element={<CaseDetail />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/summary/:id" element={<SummaryPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}
